import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';

// Layouts
import MainLayout from './components/shared/MainLayout';
import AdminLayout from './components/shared/AdminLayout';

// Guest Pages
import CatalogPage from './pages/guest/CatalogPage';
import VehicleDetailPage from './pages/guest/VehicleDetailPage';
import ReservationPage from './pages/guest/ReservationPage';
import MyReservationsPage from './pages/guest/MyReservationsPage';
import PaymentPage from './pages/guest/PaymentPage';
import ProfilePage from './pages/guest/ProfilePage';

// Host Pages
import HostDashboard from './pages/host/HostDashboard';
import VehicleRegisterPage from './pages/host/VehicleRegisterPage';
import MyVehiclesPage from './pages/host/MyVehiclesPage';
import VehicleManagePage from './pages/host/VehicleManagePage';
import HostCalendarPage from './pages/host/HostCalendarPage';
import HostEarningsPage from './pages/host/HostEarningsPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminVehiclesPage from './pages/admin/AdminVehiclesPage';
import AdminVehiclesAllPage from './pages/admin/AdminVehiclesAllPage';
import AdminReservationsPage from './pages/admin/AdminReservationsPage';
import AdminSettlementsPage from './pages/admin/AdminSettlementsPage';
import AdminConfigPage from './pages/admin/AdminConfigPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import InspectionTestPage from './pages/admin/InspectionTestPage';

// Auth Pages
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';

export default function App() {
  const user = useAuthStore(s => s.user);
  const token = useAuthStore(s => s.token);
  const loadUser = useAuthStore(s => s.loadUser);

  // Restore user session on page refresh
  useEffect(() => {
    if (token && !user) {
      loadUser();
    }
  }, []);

  // Show loading while restoring session
  if (token && !user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<CatalogPage />} />
        <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
        <Route path="/signin" element={user ? <Navigate to="/" /> : <SignInPage />} />
        <Route path="/signup" element={user ? <Navigate to="/" /> : <SignUpPage />} />
      </Route>

      {/* Guest routes (auth required) */}
      <Route element={<MainLayout requireAuth />}>
        <Route path="/reservations/new/:vehicleId" element={<ReservationPage />} />
        <Route path="/reservations" element={<MyReservationsPage />} />
        <Route path="/reservations/:id" element={<MyReservationsPage />} />
        <Route path="/reservations/:id/payment" element={<PaymentPage />} />
        <Route path="/reservations/:id/inspection" element={<InspectionTestPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Host routes */}
      <Route element={<MainLayout requireAuth requireRole="host" />}>
        <Route path="/host" element={<HostDashboard />} />
        <Route path="/host/vehicles" element={<MyVehiclesPage />} />
        <Route path="/host/vehicles/:id/manage" element={<VehicleManagePage />} />
        <Route path="/host/vehicles/new" element={<VehicleRegisterPage />} />
        <Route path="/host/calendar" element={<HostCalendarPage />} />
        <Route path="/host/earnings" element={<HostEarningsPage />} />
      </Route>

      {/* Admin routes */}
      <Route element={<AdminLayout />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/vehicles" element={<AdminVehiclesPage />} />
        <Route path="/admin/vehicles/all" element={<AdminVehiclesAllPage />} />
        <Route path="/admin/vehicles/:id/manage" element={<VehicleManagePage />} />
        <Route path="/admin/reservations" element={<AdminReservationsPage />} />
        <Route path="/admin/settlements" element={<AdminSettlementsPage />} />
        <Route path="/admin/config" element={<AdminConfigPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
